import React, { useRef, useState } from "react";
import styled, { css } from "styled-components";
import { redactionUid } from "./redactionUtils";

const MIN_SIZE_PX = 4;

const clamp01 = (v) => Math.min(1, Math.max(0, v));

/**
 * Black redaction boxes over one PDF page. With `editable`, dragging on the
 * page draws a new box and each box gets a remove button.
 */
const RedactionLayer = React.memo(function RedactionLayer({
  page,
  boxes,
  editable = false,
  onAdd,
  onDelete,
}) {
  const layerRef = useRef(null);
  const startRef = useRef(null);
  const draftRef = useRef(null);
  const [draft, setDraftState] = useState(null);
  const setDraft = (d) => {
    draftRef.current = d;
    setDraftState(d);
  };

  const toPoint = (e) => {
    const r = layerRef.current.getBoundingClientRect();
    return {
      x: clamp01((e.clientX - r.left) / r.width),
      y: clamp01((e.clientY - r.top) / r.height),
      w: r.width,
      h: r.height,
    };
  };

  const handlePointerDown = (e) => {
    if (!editable || e.button !== 0) return;
    e.preventDefault();
    e.stopPropagation();
    if (layerRef.current.setPointerCapture) {
      layerRef.current.setPointerCapture(e.pointerId);
    }
    startRef.current = toPoint(e);
    setDraft({ x: startRef.current.x, y: startRef.current.y, w: 0, h: 0 });
  };

  const handlePointerMove = (e) => {
    if (!startRef.current) return;
    const s = startRef.current;
    const p = toPoint(e);
    setDraft({
      x: Math.min(s.x, p.x),
      y: Math.min(s.y, p.y),
      w: Math.abs(p.x - s.x),
      h: Math.abs(p.y - s.y),
    });
  };

  const finish = (e) => {
    const s = startRef.current;
    startRef.current = null;
    if (!s) return;
    if (layerRef.current.releasePointerCapture) {
      layerRef.current.releasePointerCapture(e.pointerId);
    }
    const d = draftRef.current;
    setDraft(null);
    if (d && d.w * s.w >= MIN_SIZE_PX && d.h * s.h >= MIN_SIZE_PX && onAdd) {
      onAdd([{ id: redactionUid(), page, ...d }]);
    }
  };

  if (!editable && !boxes.length) return null;

  return (
    <Layer
      ref={layerRef}
      $editable={editable}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={finish}
      onPointerCancel={finish}
      onClick={(e) => editable && e.stopPropagation()}
      onCopy={(e) => e.preventDefault()}
    >
      {boxes.map((b) => (
        <Box
          key={b.id}
          $editable={editable}
          title={editable ? "Redacted area" : "Redacted"}
          style={{
            left: `${b.x * 100}%`,
            top: `${b.y * 100}%`,
            width: `${b.w * 100}%`,
            height: `${b.h * 100}%`,
          }}
        >
          {editable && onDelete && (
            <RemoveBtn
              type="button"
              title="Remove redaction"
              onPointerDown={(e) => e.stopPropagation()}
              onClick={(e) => {
                e.stopPropagation();
                onDelete(b.id);
              }}
            >
              ×
            </RemoveBtn>
          )}
        </Box>
      ))}
      {draft && (
        <Draft
          style={{
            left: `${draft.x * 100}%`,
            top: `${draft.y * 100}%`,
            width: `${draft.w * 100}%`,
            height: `${draft.h * 100}%`,
          }}
        />
      )}
    </Layer>
  );
});

export default RedactionLayer;

const Layer = styled.div`
  position: absolute;
  inset: 0;
  z-index: 4;
  user-select: none;
  -webkit-user-select: none;
  pointer-events: none;
  ${({ $editable }) =>
    $editable &&
    css`
      pointer-events: auto;
      cursor: crosshair;
      touch-action: none;
    `}
`;

const Box = styled.div`
  position: absolute;
  background: #000000;
  pointer-events: auto;
  user-select: none;
  -webkit-user-select: none;
  ${({ $editable }) =>
    $editable &&
    css`
      outline: 1.5px dashed #ef4444;
      outline-offset: 1px;
      cursor: default;
      &:hover > button {
        opacity: 1;
      }
    `}
`;

const RemoveBtn = styled.button`
  position: absolute;
  top: -9px;
  right: -9px;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  border: 2px solid #ffffff;
  background: #ef4444;
  color: #ffffff;
  font-size: 12px;
  line-height: 1;
  padding: 0;
  cursor: pointer;
  opacity: 0;
  transition: opacity 0.12s;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.25);
  @media (hover: none) {
    opacity: 1;
  }
`;

const Draft = styled.div`
  position: absolute;
  background: rgba(0, 0, 0, 0.55);
  outline: 1.5px dashed #ef4444;
  pointer-events: none;
`;
